import { ref } from '@vue/composition-api'

export default function useOutboundOperations(vm) {
  const state = ref({
    originalTotalOrder: 0,
    originalFulfillmentFee: new Map(),
    orderNotPackedYet: new Map(),
    orderSelected: new Map(),
    newFulfillmentFee: new Map(),
    grandFulfillmentFee: 0,
    grandTotalOrder: 0,
    onUpdate: () => {},
  })

  /**
   * Initialize the state to provide the initial state for the outbound operations
   * @public exposed
   * @param {Object} totalOrder - The total order
   * @param {Function} onUpdate - The function to update the state
   * @returns {void}
   */
  const initializeState = ({ totalOrder, onUpdate = () => {} }) => {
    state.value.originalTotalOrder = totalOrder
    state.value.orderNotPackedYet = new Map()
    state.value.orderSelected = new Map()
    state.value.newFulfillmentFee = new Map()
    state.value.onUpdate = onUpdate
  }

  /**
   * Update the selected order
   * @public exposed
   * @param {Object} selected - The selected order
   * @param {number} position - The position of the order
   * @param {boolean} isRemoved - Whether the order is removed
   * @param {number} fulfillmentFee - The fulfillment fee of the order
   * @param {number} originalFulfillmentFee - The original fulfillment fee of the order
   * @returns {void}
   */
  const updateSelectedOrder = ({
    selected, position, isRemoved, fulfillmentFee, originalFulfillmentFee: originalFulfillmentFeeProvided,
  }) => {
    if (isRemoved) {
      state.value.orderNotPackedYet.delete(position)
      state.value.orderSelected.delete(position)
      state.value.newFulfillmentFee.delete(position)
      state.value.originalFulfillmentFee.delete(position)
    } else {
      state.value.orderNotPackedYet.set(position, selected)
      state.value.orderSelected.set(position, selected.outbound_detail_id)
      state.value.newFulfillmentFee.set(position, fulfillmentFee)
      state.value.originalFulfillmentFee.set(position, originalFulfillmentFeeProvided)
    }

    const originalFulfillmentFee = Array.from(state.value.originalFulfillmentFee.values())?.reduce((acc, curr) => acc + curr, 0) || 0
    const newFulfillmentFee = Array.from(state.value.newFulfillmentFee.values())?.reduce((acc, curr) => acc + curr, 0) || 0
    const orderSelected = Array.from(state.value.orderSelected.values())?.length || 0

    state.value.grandFulfillmentFee = newFulfillmentFee
    state.value.grandTotalOrder = orderSelected

    state.value.onUpdate({
      orderSelected,
      newFulfillmentFee,
      originalFulfillmentFee,
    })
  }

  /**
   * Execute the mutation
   * @public exposed
   * @param {string} type - The type of the mutation
   * @returns {Promise<Object>} - The promise of the mutation
   */
  const executeMutation = async type => {
    let payload = {}
    if (type === 'confirm') {
      payload = {
        grand_fulfillment_fee: state.value.grandFulfillmentFee,
        grand_total_order: state.value.grandTotalOrder,
        payload: Array.from(state.value.orderNotPackedYet.values()),
      }
    } else {
      payload = {
        outbound_detail_ids: Array.from(state.value.orderSelected.values()),
      }
    }

    return vm.$http_new.put(`/kompack/api/v1/mitra/outbounds/${vm.orderData?.id}/${type}`, payload)
  }

  /**
   * Confirm the order
   * @public exposed
   * @param {Object} options - The options object
   * @param {string} options.type - The type of the mutation
   * @param {Function} options.mutationFn - The function to execute the mutation
   * @param {Function} options.errorFn - The function to handle the error
   * @returns {Promise<void>} - The promise of the mutation
   */
  const confirmOrder = async ({
    type = 'confirm', mutationFn = async () => {}, errorFn = () => {},
  }) => {
    try {
      const isAllSelected = Array.from(state.value.orderSelected.values())?.length === state.value.originalTotalOrder
      await mutationFn({
        isAllSelected,
        execMutation: async () => executeMutation(type),
      })
    } catch (error) {
      errorFn(error)
    }
  }

  return {
    state,
    initializeState,
    updateSelectedOrder,
    confirmOrder,
  }
}
